import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

export interface LocaleControllerOptions<Locale extends string, Translations> {
  locales: readonly Locale[]
  defaultLocale: Locale
  storageKey: string
  translations: Record<Locale, Translations>
  numberLocales?: Partial<Record<Locale, string>>
  resolvePathLocale?: (pathname: string) => Locale | null
}

export interface LocaleContextValue<Locale extends string, Translations> {
  locale: Locale
  t: Translations
  setLocale: (locale: Locale) => void
  setRouteLocale: (locale: Locale | null) => void
}

export function createLocaleController<Locale extends string, Translations>(
  options: LocaleControllerOptions<Locale, Translations>,
) {
  const LocaleContext = createContext<LocaleContextValue<Locale, Translations>>({
    locale: options.defaultLocale,
    t: options.translations[options.defaultLocale],
    setLocale: () => {},
    setRouteLocale: () => {},
  })

  function I18nProvider({
    children,
    initialLocale,
  }: {
    children: ReactNode
    initialLocale?: Locale
  }) {
    const [locale, setLocaleState] = useState<Locale>(() => (
      initialLocale ?? readPathLocale(options) ?? options.defaultLocale
    ))

    useEffect(() => {
      const initial = readPathLocale(options)
        ?? readStoredLocale(options)
        ?? options.defaultLocale
      if (initial !== locale) {
        setLocaleState(initial)
      }
      applyHtmlLang(initial)
    }, [])

    const setLocale = useCallback((next: Locale): void => {
      if (!isLocale(options.locales, next)) return
      writeStoredLocale(options.storageKey, next)
      applyHtmlLang(next)
      setLocaleState(next)
    }, [])

    const setRouteLocale = useCallback((next: Locale | null): void => {
      const resolved = next === null
        ? readPathLocale(options) ?? readStoredLocale(options) ?? options.defaultLocale
        : next
      if (!isLocale(options.locales, resolved)) return
      applyHtmlLang(resolved)
      setLocaleState(resolved)
    }, [])

    return (
      <LocaleContext.Provider
        value={{ locale, t: options.translations[locale], setLocale, setRouteLocale }}
      >
        {children}
      </LocaleContext.Provider>
    )
  }

  function useT(): Translations {
    return useContext(LocaleContext).t
  }

  function useLocale(): {
    locale: Locale
    setLocale: (locale: Locale) => void
    setRouteLocale: (locale: Locale | null) => void
  } {
    const { locale, setLocale, setRouteLocale } = useContext(LocaleContext)
    return { locale, setLocale, setRouteLocale }
  }

  function numberLocale(locale: Locale): string {
    return options.numberLocales?.[locale] ?? locale
  }

  return {
    I18nProvider,
    useT,
    useLocale,
    numberLocale,
    readStoredLocale: () => readStoredLocale(options),
    writeStoredLocale: (locale: Locale) => writeStoredLocale(options.storageKey, locale),
    applyHtmlLang,
    format,
  }
}

function readPathLocale<Locale extends string, Translations>(
  options: LocaleControllerOptions<Locale, Translations>,
): Locale | null {
  if (typeof window === 'undefined' || !options.resolvePathLocale) return null
  const locale = options.resolvePathLocale(window.location.pathname)
  return isLocale(options.locales, locale) ? locale : null
}

export function format(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    key in params ? String(params[key]) : `{${key}}`,
  )
}

export function applyHtmlLang(locale: string): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = locale
}

export function blogSlugFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/blog\/([^/]+)\/?$/)
  if (!match?.[1]) return null

  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}

function readStoredLocale<Locale extends string, Translations>(
  options: LocaleControllerOptions<Locale, Translations>,
): Locale | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(options.storageKey)
    return isLocale(options.locales, value) ? value : null
  } catch {
    return null
  }
}

function writeStoredLocale(storageKey: string, locale: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, locale)
  } catch {
    // localStorage may be blocked by browser settings.
  }
}

function isLocale<Locale extends string>(
  locales: readonly Locale[],
  value: string | null | undefined,
): value is Locale {
  return value !== null && value !== undefined && locales.includes(value as Locale)
}
